import { DateTime, Duration, Settings } from 'luxon';

export const setupLuxon = (locale: string) => {
  Settings.defaultLocale = locale;
};

export const formatDateRange = (
  startDate: string,
  endDate: string,
): string | null => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  if (!start?.isValid || !end?.isValid) return null;

  if (start.hasSame(end, 'year')) {
    if (start.hasSame(end, 'month')) {
      if (start.hasSame(end, 'day')) {
        return start.toFormat('d LLLL yyyy');
      }

      return `${start.toFormat('d')} - ${end.toFormat('d LLLL yyyy')}`;
    }

    return `${start.toFormat('d LLLL')} - ${end.toFormat('d LLLL yyyy')}`;
  }

  return `${start.toFormat('d LLLL yyyy')} - ${end.toFormat('d LLLL yyyy')}`;
};

export const formatDateTimeRange = (
  startDate: number,
  endDate?: number | null,
): string | null => {
  const TIME_FORMAT = 'HH:mm ZZZZ';
  const start = DateTime.fromMillis(startDate);
  if (!start?.isValid) return null;

  if (!endDate) {
    return `${start.toLocaleString(DateTime.DATE_MED)} ${start.toFormat(
      TIME_FORMAT,
    )}`;
  }

  const end = DateTime.fromMillis(endDate);

  return `${start.toLocaleString(DateTime.DATETIME_MED)} - ${end.toLocaleString(
    DateTime.DATE_MED,
  )} ${end.toFormat(TIME_FORMAT)}`;
};

export const isoTimeRelativeToNow = (date: string): string | null => {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) return null;
  return parsedDate.toRelative();
};

export const timeRelativeToNow = (
  milliseconds?: number | null,
): string | null => {
  if (!milliseconds) return null;
  return DateTime.fromMillis(milliseconds).toRelative();
};

export const toLocaleDate = (
  date: string | number,
  format = DateTime.DATETIME_MED,
) => {
  if (typeof date === 'number') {
    return DateTime.fromMillis(date).toLocaleString(format);
  }
  return DateTime.fromISO(date).toLocaleString(format);
};

export const toLocaleDateTimeLong = (date: string) => {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_FULL);
};

export const formatDate = (
  milliseconds: number,
  format: string = 'MMM d, yyyy, HH:mm',
): string | null => {
  const parsedDate = DateTime.fromMillis(milliseconds);
  if (!parsedDate.isValid) return null;
  return parsedDate.toFormat(format);
};

export const dateIsAfter = (
  date?: string | null,
  compare?: string | null,
): boolean => {
  if (!date || !compare) return false;

  const parsedDate = DateTime.fromISO(date);
  const parsedCompare = DateTime.fromISO(compare);
  return parsedDate > parsedCompare;
};

export const dateIsBefore = (
  date?: string | null,
  compare?: string | null,
): boolean => {
  if (!date || !compare) return false;

  const parsedDate = DateTime.fromISO(date);
  const parsedCompare = DateTime.fromISO(compare);
  return parsedDate < parsedCompare;
};

export const toHumanHoursAndMinutes = (seconds: number): string | null => {
  const duration = Duration.fromObject({ seconds }).shiftTo(
    'hours',
    'minutes',
    'seconds', // added so that 'minutes' does not get decimals
  );

  const { hours, minutes } = duration;

  if (hours > 0 && minutes > 0) {
    return Duration.fromObject({ hours, minutes }).toHuman();
  } else if (hours > 0 && minutes === 0) {
    return Duration.fromObject({ hours }).toHuman();
  } else if (hours < 1 && minutes > 0) {
    return Duration.fromObject({ minutes }).toHuman();
  } else {
    return null;
  }
};
